import { VCS_WEBINAR_LOCKS_SESSION_KEY } from 'consts';
import type { WebinarModeData } from 'api/types';
import { suppressNextWebinarEnableMetadataNotification } from 'useCase/webinar/webinarModeSnackbar';

export type PersistedWebinarLocks = {
    roomId?: string;
    microDisabled?: boolean;
    cameraDisabled?: boolean;
    /** true только после F5: дизейблит кнопки на prejoin. После hangup — false. */
    lockPrejoinUi?: boolean;
};

type PersistWebinarLocksOptions = {
    lockPrejoinUi?: boolean;
};

export const persistWebinarLocksForPrejoin = (
    webinarMode?: WebinarModeData,
    roomId?: string | null,
    options?: PersistWebinarLocksOptions,
): void => {
    if (!webinarMode?.enabled || (!webinarMode.microDisabled && !webinarMode.cameraDisabled)) {
        sessionStorage.removeItem(VCS_WEBINAR_LOCKS_SESSION_KEY);
        return;
    }

    sessionStorage.setItem(
        VCS_WEBINAR_LOCKS_SESSION_KEY,
        JSON.stringify({
            roomId: roomId || undefined,
            microDisabled: Boolean(webinarMode.microDisabled),
            cameraDisabled: Boolean(webinarMode.cameraDisabled),
            lockPrejoinUi: Boolean(options?.lockPrejoinUi),
        } satisfies PersistedWebinarLocks),
    );
};

export const getPersistedWebinarLocks = (roomId?: string | null): PersistedWebinarLocks | null => {
    const raw = sessionStorage.getItem(VCS_WEBINAR_LOCKS_SESSION_KEY);
    if (!raw) return null;

    try {
        const locks = JSON.parse(raw) as PersistedWebinarLocks;
        // Локи только для той же комнаты — иначе на другом звонке были бы ложные блокировки.
        if (roomId && locks.roomId && locks.roomId !== roomId) {
            return null;
        }
        return locks;
    } catch {
        return null;
    }
};

/** Гасит prefs mic/cam. Не влияет на disabled кнопок — это отдельно через lockPrejoinUi. */
export const applyPersistedWebinarMediaLocks = (
    setAudioEnabled: (enabled: boolean) => void,
    setVideoEnabled: (enabled: boolean) => void,
    roomId?: string | null,
): PersistedWebinarLocks | null => {
    const locks = getPersistedWebinarLocks(roomId);
    if (!locks) return null;

    if (locks.microDisabled) {
        setAudioEnabled(false);
    }
    if (locks.cameraDisabled) {
        setVideoEnabled(false);
    }

    return locks;
};

/** Сохраняет webinarMode из join/start API и сразу гасит mic/cam prefs до connect в LiveKit. */
export const applyWebinarModeFromJoinResponse = (
    webinarMode: WebinarModeData | undefined,
    roomId: string | null | undefined,
    setAudioEnabled: (enabled: boolean) => void,
    setVideoEnabled: (enabled: boolean) => void,
): void => {
    if (webinarMode?.enabled) {
        suppressNextWebinarEnableMetadataNotification();
    }
    persistWebinarLocksForPrejoin(webinarMode, roomId);
    applyPersistedWebinarMediaLocks(setAudioEnabled, setVideoEnabled, roomId);
};
